<template>
  <div class="campaign-summary-container">
    <!-- sidebar summary -->
    <Card
      class="bg-dark-grey h-full relative compaign-summary-side overflow-y-auto"
      :pt="{
        body: {
          class: 'p-0'
        }
      }"
    >
      <template #content>
        <div class="bg-primary p-4 pb-6 text-white border-round-top-xl">
          <h2 class="text-xl mt-0 mb-4 font-bold">
            Campaign Summary
          </h2>

          <div class="font-bold">
            {{ campaignName || '-' }}
          </div>
          <div
            v-if="startDate || endDate"
            class="mt-1 text-sm text-gray-100"
          >
            {{ formattedStartDate }}
            - {{ formattedEndDate }}
          </div>

          <Divider />

          <table class="campaign-summary-table mt-2">
            <tbody>
              <tr>
                <td>Your Budget</td>
                <td>{{ formattedBudget }}</td>
              </tr>
              <tr>
                <td>Bid CPM</td>
                <td>{{ formattedBidCpm }}</td>
              </tr>
              <tr>
                <td>Ad Length</td>
                <td>{{ adLength ? `${adLength}s` : '-' }}</td>
              </tr>
              <tr>
                <td>Ad Slot</td>
                <td><small>{{ formattedAdRunSlots }}</small></td>
              </tr>
              <tr>
                <td>Ad Type</td>
                <td><small>{{ adType || '-'}}</small></td>
              </tr>
            </tbody>
          </table>
          <Card class="bg-grey campaign-summary-impressions-card mt-2">
            <template #content>
              <div>
                Impressions
              </div>
              <div class="mt-3 flex justify-content-between">
                <div class="text-primary line-height-3">
                  <span class="text-sm font-semibold">
                    Estimated
                    <i
                      class="pi pi-info-circle ml-1 cursor-pointer text-white"
                      @click="$refs.estimatedImpressionsHelperOverlay
                        && $refs.estimatedImpressionsHelperOverlay.toggle($event)"
                      @keydown="$refs.estimatedImpressionsHelperOverlay
                        && $refs.estimatedImpressionsHelperOverlay.toggle($event)"
                    />
                  </span><br />
                  <span class="font-bold">{{ formattedEstimatedImpressions }}</span>
                </div>
                <div class="text-right line-height-3">
                  <span class="text-sm font-semibold">Target</span><br />
                  <span class="font-bold">{{ formattedTargetedImpressions }}</span>
                </div>
              </div>
              <MeterGroup
                class="mt-2"
                :value="[{
                  value: estimatedImpressionsMeterValue,
                  color: '#f26522',
                }]"
                :pt="{
                  labelList: {
                    class: 'hidden',
                  }
                }"
              />
            </template>
          </Card>
        </div>
      </template>
    </Card>

    <!-- top summary -->
    <Panel
      class="bg-primary compaign-summary-top"
      header="Campaign Summary"
      toggleable
      collapsed
      :pt="{
        header: {
          class: 'text-white',
        },
        content: {
          class: 'p-0'
        },
        pcToggleButton: {
          class: 'text-white',
        },
      }"
    >
      <div class="px-4 pt-4">
        <div class="font-bold text-white">
          {{ campaignName || '-' }}
        </div>
        <div
          v-if="startDate || endDate"
          class="mt-1 text-sm text-gray-100"
        >
          {{ formattedStartDate }}
          - {{ formattedEndDate }}
        </div>

        <Divider />
      </div>

      <table class="campaign-summary-table px-4 pt-4 mt-2 bg-primary">
        <tbody>
          <tr>
            <td>Your Budget</td>
            <td>{{ formattedBudget }}</td>
          </tr>
          <tr>
            <td>Bid CPM</td>
            <td>{{ formattedBidCpm }}</td>
          </tr>
          <tr>
            <td>Ad Length</td>
            <td>{{ adLength ? `${adLength}s` : '-' }}</td>
          </tr>
          <tr>
            <td>Ad Slot</td>
            <td><small>{{ formattedAdRunSlots }}</small></td>
          </tr>
          <tr>
            <td>Ad Type</td>
            <td><small>{{ adType || '-'}}</small></td>
          </tr>
        </tbody>
      </table>
      <Card class="bg-grey text-white border-noround-top">
        <template #content>
          <div class="text-sm">
            Impressions
          </div>
          <div class="mt-3 flex justify-content-between">
            <div class="text-primary line-height-3">
              <span class="text-sm font-semibold">
                Estimated
                <i
                  class="pi pi-info-circle ml-1 cursor-pointer text-white"
                  @click="$refs.estimatedImpressionsHelperOverlay
                    && $refs.estimatedImpressionsHelperOverlay.toggle($event)"
                  @keydown="$refs.estimatedImpressionsHelperOverlay
                    && $refs.estimatedImpressionsHelperOverlay.toggle($event)"
                />
              </span><br />
              <span class="font-bold">{{ formattedEstimatedImpressions }}</span>
            </div>
            <div class="text-right line-height-3">
              <span class="text-sm font-semibold">Target</span><br />
              <span class="font-bold">{{ formattedTargetedImpressions }}</span>
            </div>
          </div>
          <MeterGroup
            class="mt-2"
            :value="[{
              value: estimatedImpressionsMeterValue,
              color: '#f26522',
            }]"
            :pt="{
              labelList: {
                class: 'hidden',
              }
            }"
          />
        </template>
      </Card>
    </Panel>

    <OverlayPanel
      class="hide-overlaypanel-triangle"
      ref="estimatedImpressionsHelperOverlay"
    >
      <div
        :style="{
          maxWidth: '320px',
        }"
        class="text-sm"
      >
        <p class="mt-0">
          Estimated impressions are the amount of impressions we
          believe we can fufill based on your campaign parameters.
        </p>
        <p>
          To increase your estimated impressions you can try the following
        </p>
        <ul class="mb-0">
          <li class="mb-1">Increase the duration of your campaign</li>
          <li class="mb-1">Add additional podcast categories</li>
          <li class="mb-1">Select "All" for Target Gender (if not selected)</li>
          <li>Select "All" or additional age groups</li>
        </ul>
      </div>
    </OverlayPanel>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { AD_RUN_SLOT_OPTIONS } from '@/constants';
import { calculateImpressions } from '@/utils/impressions';

export default {
  props: {
    campaignName: {
      type: String,
    },
    startDate: {
      validator: (prop) => typeof prop === 'object' || prop === null,
    },
    endDate: {
      validator: (prop) => typeof prop === 'object' || prop === null,
    },
    budget: {
      validator: (prop) => typeof prop === 'number' || prop === null,
    },
    bidCpm: {
      validator: (prop) => typeof prop === 'number' || prop === null,
    },
    adRunSlots: {
      validator: (prop) => typeof prop === 'object' || prop === null,
    },
    adLength: {
      validator: (prop) => typeof prop === 'number' || prop === null,
    },
    adType: {
      validator: (prop) => typeof prop === 'string' || prop === null,
    },
    estimatedImpressions: {
      type: Number,
    },
  },
  computed: {
    formattedBudget() {
      return typeof this.budget === 'number'
        ? this.budget.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          style: 'currency',
          currency: 'USD',
        }).toString()
        : '-';
    },
    formattedBidCpm() {
      return typeof this.bidCpm === 'number'
        ? this.bidCpm.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          style: 'currency',
          currency: 'USD',
        }).toString()
        : '-';
    },
    formattedAdRunSlots() {
      if (!this.adRunSlots || this.adRunSlots.length === 0) {
        return '-';
      }

      return this.adRunSlots.map((slot) => {
        const matchingAdRunSlot = AD_RUN_SLOT_OPTIONS
          .find((item) => item.value === slot);

        return matchingAdRunSlot ? matchingAdRunSlot.label : slot;
      }).join(', ');
    },
    formattedTargetedImpressions() {
      if (typeof this.budget !== 'number' || typeof this.bidCpm !== 'number') {
        return '-';
      }

      const targetedImpressions = calculateImpressions(this.budget, this.bidCpm);

      return targetedImpressions.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        notation: 'compact',
        compactDisplay: 'short',
      });
    },
    formattedEstimatedImpressions() {
      if (typeof this.budget !== 'number' || typeof this.bidCpm !== 'number') {
        return '0';
      }

      const targetedImpressions = calculateImpressions(this.budget, this.bidCpm);

      const maxEstimatedImpressions = Math.min(targetedImpressions, this.estimatedImpressions);

      return maxEstimatedImpressions.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        notation: 'compact',
        compactDisplay: 'short',
      });
    },
    formattedStartDate() {
      return this.startDate
        ? DateTime.fromJSDate(this.startDate).toLocaleString({ month: 'short', day: 'numeric' })
        : '';
    },
    formattedEndDate() {
      return this.endDate
        ? DateTime.fromJSDate(this.endDate).toLocaleString({ month: 'short', day: 'numeric' })
        : '';
    },
    estimatedImpressionsMeterValue() {
      if (typeof this.budget !== 'number' || typeof this.bidCpm !== 'number') {
        return 0;
      }

      const targetedImpressions = calculateImpressions(this.budget, this.bidCpm);

      if (targetedImpressions === 0) {
        return 0;
      }

      const fufilledImpressionPercent = (this.estimatedImpressions / targetedImpressions) * 100;

      return Math.min(fufilledImpressionPercent, 100);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.campaign-summary-container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: auto;
  left: 0;
  width: 100%;
  padding: 0.5rem;
  z-index: 1;

  @media screen and (
    min-width: #{map-get($breakpoints, 'md')}
  ) {
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    width: 260px;
  }
  @media screen and (
    min-width: #{map-get($breakpoints, 'lg')}
  ) {
    width: 300px;
  }
}
.compaign-summary-side {
  display: none;

  @media screen and (
    min-width: #{map-get($breakpoints, 'md')}
  ) {
    display: block;
  }
}
.compaign-summary-top {
  display: block;

  :deep(.p-panel-header-icon) {
    color: white;
  }

  @media screen and (
    min-width: #{map-get($breakpoints, 'md')}
  ) {
    display: none;
  }
}
.campaign-summary-table {
  width: 100%;
  tbody {
    tr {
      td {
        padding-bottom: 0.4375rem;
      }

      td:nth-child(1) {
        color: var(--gray-100);
        font-size: 0.875rem;
      }
      td:nth-child(2) {
        text-align: right;
        color: white;
      }
    }
  }
}
.campaign-summary-impressions-card {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  color: white;
}
</style>
